import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { getTodos } from '../../api/todoApi';
import TodoViewModal from '../../components/todo/TodoViewModal';
import '../../css/todo.css';

const DATE_FILTERS = [
  { value: 'today', label: '오늘' },
  { value: 'upcoming', label: '예정' },
  { value: 'no_due', label: '기한없음' },
  { value: 'overdue', label: '기한지남' },
];

const STATUS_FILTERS = [
  { value: 'incomplete', label: '미완료' },
  { value: 'complete', label: '완료' },
  { value: 'all', label: '전체' },
];

const REF_TYPE_LABEL = {
  board: '게시',
  approval: '결재',
  resource: '자원',
  schedule: '일정',
  pms_checklist: '프로젝트',
  pms_comment: '프로젝트',
  pms_feed: '프로젝트',
};

const REF_TYPE_BADGE = {
  board: 'bg-info',
  approval: 'bg-warning text-dark',
  resource: 'bg-success',
  schedule: 'bg-primary',
  pms_checklist: 'bg-pms',
  pms_comment: 'bg-pms',
  pms_feed: 'bg-pms',
};


const PRIORITY_COLOR = {
  high: 'text-danger',
  normal: 'text-secondary',
  low: 'text-muted',
};

// To-Do 포틀릿 기본/증가 갯수 상수
const DEFAULT_LIMIT = 4;

function TodoCard({ onShowOriginal }) {
  const [todos, setTodos] = useState([]);
  const [dateFilter, setDateFilter] = useState('today');
  const [statusFilter, setStatusFilter] = useState('incomplete');
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [limit, setLimit] = useState(DEFAULT_LIMIT); // 기본값 상수 사용
  const token = sessionStorage.getItem('token');
  const navigate = useNavigate();
  const showStatusCol = statusFilter === 'all';

  // 전체 todos를 받아오고, 렌더링은 limit만큼만
  const fetchTodos = useCallback(async () => {
    try {
      // 서버에서 충분히 넉넉하게 받아옴(최대 100개)
      const { todos: data } = await getTodos({ dateFilter, status: statusFilter, limit: 100 }, token);
      setTodos(data || []);
      setLimit(DEFAULT_LIMIT); // 필터 변경 시 limit 초기화
    } catch (err) {
      console.error('[TodoCard] 조회 실패:', err);
    }
  }, [dateFilter, statusFilter, token]);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const getDueDateStyle = (todo) => {
    if (!todo.due_date || todo.due_type === 'none') return '';
    if (todo.due_date < today) return 'text-danger fw-bold';
    if (todo.due_date === today) return 'text-warning fw-bold';
    return '';
  };

  const getDueDateLabel = (todo) => {
    if (!todo.due_date || todo.due_type === 'none') return '기한없음';
    if (todo.due_date === today) return '오늘';
    return todo.due_date;
  };

  return (
    <div className="card mb-3 flex-grow-1 todo-card">
      <div className="card-body">
        {/* 제목 + 더보기 */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-end">
            <h4 className="mb-0">내 To-Do</h4>
            <span
              style={{
                fontSize: '11px',
                marginLeft: '0.75rem',
                color: '#1976d2',
                fontWeight: 600,
                background: '#e0f7fa',
                borderRadius: '0.75rem',
                padding: '0.15em 0.8em',
                border: 'none',
                lineHeight: 1.2,
                display: 'inline-block',
                boxShadow: '0 1px 4px 0 rgba(25, 118, 210, 0.07)',
              }}
            >
              총 {todos.length}건
            </span>
          </div>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => navigate('/todo')}
            type="button"
          >
            더보기
          </button>
        </div>

        {/* 필터 버튼 영역 */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="todo-btn-group">
            {DATE_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`todo-filter-btn ${dateFilter === f.value ? 'active' : ''}`}
                onClick={() => setDateFilter(f.value)}
                type="button"
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="todo-btn-group">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`todo-status-btn ${statusFilter === f.value ? 'active' : ''}`}
                onClick={() => setStatusFilter(f.value)}
                type="button"
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* 스크롤 영역 - card-body 안에 위치 */}
        <div className="todo-card-scroll">
          <table className="table table-nowrap align-middle table-hover mb-0" style={{ tableLayout: 'fixed' }}>
            <thead className="table-light todo-card-thead">
              <tr>
                <th style={{ width: 'auto' }}>제목</th>
                <th className="text-center" style={{ width: '55px' }}>구분</th>
                <th className="text-center" style={{ width: '90px' }}>기한</th>
                {showStatusCol && (
                  <th className="text-center" style={{ width: '65px' }}>상태</th>
                )}
              </tr>
            </thead>
            <tbody>
              {todos.length === 0 ? (
                <tr>
                  <td colSpan={showStatusCol ? 4 : 3} className="text-center text-muted py-3">
                    To-Do 목록이 존재하지 않습니다.
                  </td>
                </tr>
              ) : (
                todos.slice(0, limit).map((todo) => (
                  <tr
                    key={todo.todo_id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedTodo(todo)}
                  >
                    <td style={{ width: 'auto' }}>
                      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <span className={PRIORITY_COLOR[todo.priority]} style={{ flex: 'none', marginRight: todo.priority === 'high' ? '6px' : '0' }}>
                          {todo.priority === 'high' ? '●' : ''}
                        </span>
                        <span
                          className={todo.status === 'complete' ? 'text-decoration-line-through text-muted' : ''}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            verticalAlign: 'middle',
                            fontSize: 'inherit',
                          }}
                          title={todo.title}
                        >
                          {todo.title}
                        </span>
                      </div>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${REF_TYPE_BADGE[todo.ref_type] || 'bg-secondary'}`}>
                        {REF_TYPE_LABEL[todo.ref_type] || todo.ref_type}
                      </span>
                    </td>
                    <td className={`text-center ${getDueDateStyle(todo)}`}>
                      {getDueDateLabel(todo)}
                    </td>
                    {showStatusCol && (
                      <td className="text-center">
                        <span className={`badge ${todo.status === 'complete' ? 'bg-success' : 'bg-warning text-dark'}`}>
                          {todo.status === 'complete' ? '완료' : '미완료'}
                        </span>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 더보기+ 버튼 */}
        {todos.length > limit && (
          <div className="d-flex justify-content-center my-2">
            <button
              className="btn btn-link p-0 fw-bold"
              style={{ border: 'none', boxShadow: 'none', background: 'transparent', borderRadius: 0 }}
              onClick={() => setLimit((prev) => prev + DEFAULT_LIMIT)}
              type="button"
            >
              더보기 +
            </button>
          </div>
        )}
      </div>

      {/* 조회 모달 */}
      {selectedTodo && createPortal(
        <TodoViewModal
          todo={selectedTodo}
          onClose={() => setSelectedTodo(null)}
          onUpdated={() => { setSelectedTodo(null); fetchTodos(); }}
          onDeleted={() => { setSelectedTodo(null); fetchTodos(); }}
          onShowOriginal={(type, id) => {
            if (type === 'board') {
              // 모든 후보에서 codeId를 최대한 안전하게 추출
              const codeId = selectedTodo.ref_code_id || selectedTodo.code_id || selectedTodo.board_code_id || selectedTodo.board_id || null;
              onShowOriginal(type, id, codeId);
            } else {
              onShowOriginal(type, id);
            }
          }}
        />,
        document.body
      )}
    </div>
  );
}

export default TodoCard;
